/**
 * iter84.54 — Values Engine card for the Release Ops tab.
 * iter84.56.3 — Migrated to unified CollapsibleCard shell.
 *
 * Renders /api/release-ops/values-check as a list of 10 signal rows
 * with an overall verdict pill. Refresh button. Auto-fetches on
 * mount. Color rules now come from CollapsibleCard's verdict prop:
 *   overall = "clear"        → emerald outline
 *   overall = "ack_required" → amber outline   (warnings present)
 *   overall = "block_arm"    → rose outline    (high-severity fails)
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity, RefreshCw, AlertTriangle, Loader2 } from "lucide-react";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";
import { CopyDiagButton, buildDiagText, diagLine } from "@/components/site-admin/release-ops/CopyDiagButton";
import { WhatsThisButton } from "@/components/site-admin/release-ops/WhatsThisButton";
import { PendingClassificationList } from "@/components/site-admin/PendingClassificationList";
import { VERDICT_PILL, POSTURE_PILL } from "@/components/site-admin/values-uat/pills";
import { SignalRow } from "@/components/site-admin/values-uat/SignalRow";

export function ValuesEngineCard({ environment = "preview", autoPollMs = 0, onVerdictResolved }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    // iter84.60.7 — registry pending-classification state. Visible warning
    // only (tech-debt pattern); never feeds the gate verdict.
    const [registry, setRegistry] = useState(null);
    const [pendingOpen, setPendingOpen] = useState(false);

    const fetchRegistry = useCallback(async () => {
        try {
            setRegistry(await api.releaseOpsValuesRegistry());
        } catch (_e) { /* non-blocking — pending chip just stays hidden */ }
    }, []);

    useEffect(() => { fetchRegistry(); }, [fetchRegistry]);

    const fetchOnce = useCallback(async (showSpinner) => {
        if (showSpinner) setRefreshing(true);
        try {
            // iter84.61 — grouped-by-value engine view (superset of the
            // old values-check payload: same overall/summary semantics).
            const r = await api.releaseOpsValuesEngine();
            setData(r);
            setError(null);
            // iter84.56.7-g — Hand the verdict back so the parent can
            // auto-tick the values_check_preview gate. Same handshake
            // pattern as Parity Sweep / Deployment Readiness.
            if (onVerdictResolved) {
                onVerdictResolved({
                    overall: r.overall,
                    summary: r.summary || {},
                });
            }
        } catch (e) {
            setError(e.response?.data?.detail || "Failed to load values-check");
            if (onVerdictResolved) {
                onVerdictResolved({ overall: "block_arm", summary: {} });
            }
        } finally {
            setRefreshing(false);
            setLoading(false);
        }
    }, [onVerdictResolved]);

    useEffect(() => { fetchOnce(false); }, [fetchOnce]);

    // iter84.56 — Auto-poll while ARMED. autoPollMs=60000 catches the
    // exact iter84.46 failure mode: .env reverts between ARM and Deploy
    // (the silent window where human eyes are off the gate).
    useEffect(() => {
        if (!autoPollMs || autoPollMs <= 0) return undefined;
        const id = setInterval(() => fetchOnce(false), autoPollMs);
        return () => clearInterval(id);
    }, [autoPollMs, fetchOnce]);

    // iter84.56.3 — Verdict mapping. block_arm → "fail" in CollapsibleCard's
    // vocabulary (rose outline + auto-open). ack_required → "warn".
    const apiVerdict = data?.overall || (error ? "block_arm" : "clear");
    const verdict = apiVerdict === "block_arm"
        ? "fail"
        : apiVerdict === "ack_required"
            ? "warn"
            : "clear";
    const pillMeta = VERDICT_PILL[apiVerdict] || VERDICT_PILL.clear;
    const summary = data?.summary;
    const pendingCount = registry?.pending_count || 0;

    const headerExtra = (
        <span className="flex items-center gap-1">
            <WhatsThisButton
                testId="values-engine-whats-this"
                title="Values Engine"
                what="Checks that we followed our own engineering rules for this release: changelog written, UAT runbook attested, tenant isolation intact, docs not drifting, tech debt managed. It's about discipline, not data."
                lookFor="CLEAR. Anything else names exactly which rule needs attention — fix it or waive it with a reason."
                notFor="Game data (Pre-deploy sweep) or server/config health (Deployment Readiness)."
            />
            {data && (
                <CopyDiagButton
                    testId="values-engine-copy-diag-btn"
                    getText={() => buildDiagText({
                        card: "Values Engine",
                        environment,
                        verdict: data.overall,
                        summary: `${summary ? `${summary.passed}/${summary.total} pass` : ""}${pendingCount ? ` · ${pendingCount} pending classification` : ""}`,
                        lines: [...(data.groups || []).flatMap((g) => g.signals || []), ...(data.ungrouped || [])]
                            .filter((s) => s.status !== "pass")
                            .map((s) => diagLine(s.status, `${s.id}${s.severity ? ` [${s.severity}]` : ""}`, s.label !== s.id ? s.label : "")),
                    })}
                />
            )}
            <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); fetchOnce(true); fetchRegistry(); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); fetchOnce(true); fetchRegistry(); } }}
                data-testid="values-engine-refresh-btn"
                className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer"
                title="Re-run all 10 signals"
            >
                {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Refresh
            </span>
        </span>
    );

    // Build the badge: verdict label + summary counts + pending warning.
    const badge = data
        ? `${pillMeta.label}${summary ? ` · ${summary.passed}/${summary.total}` : ""}${pendingCount ? ` · ${pendingCount} pending classification` : ""}`
        : (environment === "production" ? "production" : "preview");
    const badgeClass = data ? pillMeta.pill : "bg-zinc-700/30 text-zinc-300 border-zinc-700";

    return (
        <CollapsibleCard
            testId="values-engine-card"
            icon={Activity}
            title="Values Engine"
            badge={badge}
            badgeClass={badgeClass}
            verdict={verdict}
            headerExtra={headerExtra}
        >
            <div
                data-environment={environment}
                data-overall={data?.overall || "loading"}
                data-testid="values-engine-verdict-pill"
            >
                {autoPollMs > 0 && (
                    <p
                        data-testid="values-engine-autopoll-indicator"
                        className="text-[10px] text-volt font-mono mb-2"
                    >
                        LIVE · auto-refreshing every {Math.round(autoPollMs/1000)}s (promotion ARMED)
                    </p>
                )}
                {loading && (
                    <p className="text-xs text-zinc-500 italic flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Loading signals…
                    </p>
                )}
                {error && (
                    <p className="text-xs text-rose-400" data-testid="values-engine-error">
                        {error}
                    </p>
                )}
                {!loading && !error && data && (
                    <div className="space-y-3" data-testid="values-engine-signals">
                        {(data.groups || []).map((g) => (
                            <div
                                key={g.value_group}
                                className="border border-zinc-800 rounded-md p-2 bg-black/30"
                                data-testid={`values-engine-group-${g.value_group.replace(/[^a-z0-9-]/gi, "_")}`}
                            >
                                <div className="flex items-center gap-2 flex-wrap mb-1" title={g.statement}>
                                    <span className="font-display font-extrabold text-[10px] uppercase tracking-wide text-fuchsia-200">
                                        {g.value_group}
                                    </span>
                                    <span
                                        className={`pill text-[9px] font-bold uppercase border ${POSTURE_PILL[g.posture] || POSTURE_PILL.warn}`}
                                        data-testid={`values-engine-group-posture-${g.value_group.replace(/[^a-z0-9-]/gi, "_")}`}
                                    >
                                        {g.posture === "blocked" ? "risk: blocked" : g.posture}
                                    </span>
                                    {g.static_count > 0 && (
                                        <span className="ml-auto text-[9px] text-zinc-600">
                                            +{g.static_count} static gate{g.static_count === 1 ? "" : "s"}
                                        </span>
                                    )}
                                </div>
                                {g.signals.length === 0 ? (
                                    <p className="text-[10px] text-zinc-600 italic">No live signals — static alignment only.</p>
                                ) : (
                                    <ul className="space-y-1">
                                        {g.signals.map((s) => (
                                            <SignalRow key={s.id} s={s} onChanged={() => fetchOnce(false)} />
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                        {(data.ungrouped || []).length > 0 && (
                            <div className="border border-dashed border-zinc-800 rounded-md p-2" data-testid="values-engine-ungrouped">
                                <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-bold mb-1">
                                    Unaligned signals
                                </div>
                                <ul className="space-y-1">
                                    {(data.ungrouped || []).map((s) => (
                                        <SignalRow key={s.id} s={s} onChanged={() => fetchOnce(false)} />
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {/* iter86.86 — Canon actions live in MAESTRO only. Release
                    Ops shows the pending count and links across; the inline
                    accept/override review was removed (operator direction:
                    canon items are dispositioned in Maestro, Release Ops
                    keeps only signal/check/gate approvals). */}
                {pendingCount > 0 && (
                    <div
                        className="mt-3 border border-amber-500/40 bg-amber-500/5 rounded p-2"
                        data-testid="values-engine-pending-section"
                    >
                        <div className="flex items-center gap-2 text-[11px] font-bold text-amber-300">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                            <span data-testid="values-engine-pending-count">
                                {pendingCount} registry {pendingCount === 1 ? "entry" : "entries"} pending value classification
                            </span>
                            <button
                                type="button"
                                onClick={() => window.dispatchEvent(new CustomEvent("tcr:platform-nav", { detail: { tab: "stewardship" } }))}
                                data-testid="values-engine-pending-maestro-link"
                                className="ml-auto text-[9px] uppercase tracking-wider font-bold text-amber-200 border border-amber-500/40 hover:border-amber-300 px-1.5 py-0.5 rounded"
                            >
                                Review in Maestro →
                            </button>
                        </div>
                        <p className="text-[9px] text-zinc-500 mt-1">
                            Visible warning only — does not block Phase 1. Classification is a canon decision: accept or override in Maestro → Registry Decisions (audit-logged there).
                        </p>
                    </div>
                )}
            </div>
        </CollapsibleCard>
    );
}
